import { ref, reactive, getCurrentInstance } from '@vue/composition-api'

const PER_PAGE_OPTIONS = [20, 50, 100, 200]

/**
 * Server-side list plumbing shared by the optimisation of the Horses / Drafts / Members list
 * pages (CAM-1478 / CAM-1479 / CAM-1477): pagination, 3-state sort, per-page, search and any
 * number of extra scalar filters — all fetched from the API and mirrored into the URL query so
 * a reload or "open in new tab" restores the exact same view.
 *
 * @param {(params: object) => Promise} fetchFn  API call, receives the built query params,
 *   must resolve to `{ data: { data: [], meta: { current_page, last_page, total } } }`.
 * @param {object} options
 * @param {string|null} options.defaultSort       column key sorted by default (the "none" state)
 * @param {'asc'|'desc'} options.defaultDirection
 * @param {number} options.defaultPerPage         one of 20 / 50 / 100 / 200
 * @param {object} options.extraFilters           { queryKey: defaultValue } — synced + sent
 * @param {string[]} options.stringMultiFilters    keys (from extraFilters) whose multi-value
 *   state is a string[] (e.g. `relation: ['owner','rider']`) rather than the default number[]
 *   (`zone_id: [1,2]`) — both an empty-array default, so the element type can't be inferred.
 */
export default function useServerList(fetchFn, {
  defaultSort = null,
  defaultDirection = 'asc',
  defaultPerPage = 20,
  extraFilters = {},
  stringMultiFilters = []
} = {}) {
  const { proxy } = getCurrentInstance()
  const router = proxy.$router

  const rows = ref([])
  const loading = ref(false)
  const page = ref(1)
  const totalPages = ref(1)
  const total = ref(0)

  // An extra filter whose default is an array is multi-value: it travels as a comma-separated
  // string in the URL query and the API request (`?zone_id=1,2`), and is held here as a
  // number[] (or, for a key listed in stringMultiFilters, a string[]). Everything else stays a
  // plain scalar.
  const isMulti = key => Array.isArray(extraFilters[key])
  const isStringMulti = key => stringMultiFilters.includes(key)
  const cloneDefault = key => (isMulti(key) ? [...extraFilters[key]] : extraFilters[key])

  function toNumArray(raw) {
    const parts = Array.isArray(raw) ? raw : String(raw).split(',')
    return parts.map(Number).filter(n => Number.isFinite(n) && n !== 0)
  }

  function toStrArray(raw) {
    const parts = Array.isArray(raw) ? raw : String(raw).split(',')
    return parts.map(String).filter(s => s !== '')
  }

  const filters = reactive({
    sort: defaultSort,
    direction: defaultDirection,
    per_page: defaultPerPage,
    search: '',
    ...Object.keys(extraFilters).reduce((acc, key) => {
      acc[key] = cloneDefault(key)
      return acc
    }, {})
  })

  // (Re)read the whole table state from the current URL query — called on init and again on every
  // route change, so navigating between two pages that share this component (My Horses <-> Horses)
  // reloads the right view instead of showing the previous one.
  function syncFromQuery() {
    const query = proxy.$route.query
    page.value = Number(query.page) > 0 ? Number(query.page) : 1
    filters.sort = query.sort || defaultSort
    filters.direction = query.direction === 'desc' ? 'desc' : (query.sort ? 'asc' : defaultDirection)
    const qpp = Number(query.per_page)
    filters.per_page = PER_PAGE_OPTIONS.includes(qpp) ? qpp : defaultPerPage
    filters.search = query.search || ''
    Object.keys(extraFilters).forEach((key) => {
      if (isMulti(key)) {
        filters[key] = query[key] != null ? (isStringMulti(key) ? toStrArray(query[key]) : toNumArray(query[key])) : []
      } else {
        filters[key] = query[key] != null ? query[key] : extraFilters[key]
      }
    })
  }

  syncFromQuery()

  function syncQuery() {
    const query = {}
    if (page.value > 1) query.page = String(page.value)
    if (filters.sort) {
      query.sort = filters.sort
      query.direction = filters.direction
    }
    if (filters.per_page !== defaultPerPage) query.per_page = String(filters.per_page)
    if (filters.search) query.search = filters.search
    Object.keys(extraFilters).forEach((key) => {
      if (isMulti(key)) {
        if (filters[key] && filters[key].length) query[key] = filters[key].join(',')
      } else if (filters[key] != null && String(filters[key]) !== String(extraFilters[key])) {
        query[key] = String(filters[key])
      }
    })
    // vue-router's location normalisation drops `hash` entirely when the target location has
    // no `path`/`name` of its own and no `hash` is given — it does NOT fall back to the current
    // route's hash the way it does for `path`. Harmless for the plain list pages (no hash), but
    // this composable also backs the Member Profile "Horses" tab (CAM-1505), whose parent tracks
    // the selected tab via `$route.hash` — losing it would silently bounce the user back to the
    // Details tab on the first filter/sort/page click. Pass the current hash through explicitly.
    const location = proxy.$route.hash ? { query, hash: proxy.$route.hash } : { query }
    router.replace(location).catch(() => {})
  }

  function buildParams() {
    const params = { page: page.value, per_page: filters.per_page }
    if (filters.sort) {
      params.sort = filters.sort
      params.direction = filters.direction
    }
    if (filters.search) params.search = filters.search
    Object.keys(extraFilters).forEach((key) => {
      if (isMulti(key)) {
        if (filters[key] && filters[key].length) params[key] = filters[key].join(',')
      } else if (filters[key] != null && filters[key] !== '') {
        params[key] = filters[key]
      }
    })
    return params
  }

  async function fetch() {
    loading.value = true
    try {
      const res = await fetchFn(buildParams())
      const body = res.data != null ? res.data : {}
      rows.value = body.data != null ? body.data : []
      const meta = body.meta != null ? body.meta : {}
      page.value = meta.current_page != null ? meta.current_page : 1
      totalPages.value = meta.last_page != null ? meta.last_page : 1
      total.value = meta.total != null ? meta.total : 0
    } catch (_) {
      rows.value = []
    } finally {
      loading.value = false
    }
  }

  function setPage(p) {
    page.value = p
    syncQuery()
    fetch()
  }

  // 3-state cycle — none -> asc -> desc -> none (table.md)
  function setSort(field) {
    if (filters.sort !== field) {
      filters.sort = field
      filters.direction = 'asc'
    } else if (filters.direction === 'asc') {
      filters.direction = 'desc'
    } else {
      filters.sort = defaultSort
      filters.direction = defaultDirection
    }
    page.value = 1
    syncQuery()
    fetch()
  }

  function setPerPage(n) {
    filters.per_page = Number(n)
    page.value = 1
    syncQuery()
    fetch()
  }

  function setSearch(value) {
    filters.search = value || ''
    page.value = 1
    syncQuery()
    fetch()
  }

  function setFilter(key, value) {
    filters[key] = value
    page.value = 1
    syncQuery()
    fetch()
  }

  // Set several filters at once with a single syncQuery()/fetch() — use instead of multiple
  // setFilter() calls in a row (e.g. a group of mutually-exclusive checkboxes clearing each other
  // when one is checked), which would otherwise fire one request per call.
  function setFilters(updates) {
    Object.keys(updates).forEach((key) => { filters[key] = updates[key] })
    page.value = 1
    syncQuery()
    fetch()
  }

  // Reset one extra filter to its default ("clear this filter" — the × on a single control).
  function clearFilter(key) {
    if (!(key in extraFilters)) return
    setFilter(key, cloneDefault(key))
  }

  // Reset search + every extra filter to defaults ("clear all filters"). Sort and page size
  // are view preferences, not filters, so they stay.
  function clearFilters() {
    filters.search = ''
    Object.keys(extraFilters).forEach((key) => { filters[key] = cloneDefault(key) })
    page.value = 1
    syncQuery()
    fetch()
  }

  return {
    rows,
    loading,
    page,
    totalPages,
    total,
    filters,
    perPageOptions: PER_PAGE_OPTIONS,
    fetch,
    setPage,
    setSort,
    setPerPage,
    setSearch,
    setFilter,
    setFilters,
    clearFilter,
    clearFilters,
    syncQuery,
    syncFromQuery
  }
}



// WEBPACK FOOTER //
// ./src/composables/useServerList.js